import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Mic from '@mui/icons-material/Mic';
import MicOff from '@mui/icons-material/MicOff';
import { Box, ClickAwayListener, IconButton, Popper, Tooltip } from '@mui/material';
import { alpha, keyframes, styled } from '@mui/material/styles';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { I18n } from 'utils/i18n';

const vcPop = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const Group = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  padding: '2px',
  borderRadius: '999px',
  backgroundColor: active ? alpha(theme.palette.text.primary, 0.06) : 'transparent',
  transition: 'background-color .16s ease',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

const ChevronSlot = styled(Box, {
  shouldForwardProp: prop => prop !== 'shown',
})(({ shown }) => ({
  display: 'inline-flex',
  overflow: 'hidden',
  maxWidth: shown ? 32 : 0,
  opacity: shown ? 1 : 0,
  transition: 'max-width .16s ease, opacity .16s ease',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

const ChevronButton = styled(IconButton)(({ theme }) => ({
  width: 28,
  height: 28,
  padding: 0,
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MicButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'muted',
})(({ theme, muted }) => ({
  width: 36,
  height: 36,
  padding: 0,
  backgroundColor: muted ? alpha(theme.palette.error.main, 0.9) : 'transparent',
  color: muted ? theme.palette.common.white : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: muted ? theme.palette.error.main : theme.palette.action.hover,
  },
}));

const MenuPaper = styled(Box)(({ theme }) => ({
  minWidth: 280,
  padding: '6px 0',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  boxShadow: theme.shadows[6],
  animation: `${vcPop} 140ms cubic-bezier(.2,.8,.25,1) both`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

const GroupSection = styled(Box)({
  padding: '4px 0',
});

const GroupLabel = styled(Box)(({ theme }) => ({
  padding: '4px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
}));

const DeviceItem = styled('button', {
  shouldForwardProp: prop => prop !== 'active',
})(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '7px 14px',
  fontSize: 13,
  lineHeight: 1.3,
  color: theme.palette.text.primary,
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  width: '100%',
  textAlign: 'left',
  fontWeight: active ? 600 : 400,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DeviceCheck = styled('span')(({ theme }) => ({
  width: 14,
  flexShrink: 0,
  fontSize: 13,
  color: theme.palette.primary.main,
}));

const DeviceLabel = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const MeterDivider = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '6px 14px',
  padding: '6px 0',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const MeterTrack = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: 1,
  height: 4,
  borderRadius: '999px',
  backgroundColor: alpha(theme.palette.text.secondary, 0.25),
  overflow: 'hidden',
}));

const MeterFill = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  width: 'calc(var(--vc-level, 0) * 100%)',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '999px',
  transition: 'width .08s linear',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

const DeviceList = ({ label, items, activeId, kind, onSwitchDevice }) => (
  <GroupSection>
    <GroupLabel>{label}</GroupLabel>
    {items.map(device => {
      const active = device.deviceId === activeId;
      return (
        <DeviceItem
          type="button"
          key={device.deviceId}
          active={active}
          onClick={() => onSwitchDevice(kind, device.deviceId)}
        >
          <DeviceCheck>{active ? '✓' : ''}</DeviceCheck>
          <DeviceLabel>{device.label}</DeviceLabel>
        </DeviceItem>
      );
    })}
  </GroupSection>
);

DeviceList.propTypes = {
  label: string.isRequired,
  items: arrayOf(shape({ deviceId: string, label: string })).isRequired,
  activeId: string,
  kind: string.isRequired,
  onSwitchDevice: func.isRequired,
};

const MicControl = ({
  muted = false,
  onToggleMute,
  devices = { inputs: [], outputs: [], activeInput: undefined, activeOutput: undefined },
  onSwitchDevice,
}) => {
  const anchorRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);

  const inputs = devices?.inputs || [];
  const outputs = devices?.outputs || [];

  const handleSwitch = useCallback(
    (kind, deviceId) => {
      if (onSwitchDevice) onSwitchDevice(kind, deviceId);
    },
    [onSwitchDevice],
  );

  const handleToggleOpen = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const title = muted ? I18n.t('VoiceMode.unmute') : I18n.t('VoiceMode.mute');
  const chevronShown = hovered || open;

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ display: 'inline-flex' }}
    >
      <Group active={chevronShown}>
        <ChevronSlot shown={chevronShown}>
          <ChevronButton
            type="button"
            onClick={handleToggleOpen}
            aria-label={I18n.t('VoiceMode.microphone')}
            aria-haspopup="menu"
            aria-expanded={open}
            tabIndex={chevronShown ? 0 : -1}
          >
            <KeyboardArrowDown sx={{ fontSize: 18 }} />
          </ChevronButton>
        </ChevronSlot>

        <Tooltip title={title} disableInteractive>
          <MicButton
            ref={anchorRef}
            type="button"
            muted={muted}
            onClick={onToggleMute}
            aria-label={title}
            aria-pressed={muted}
          >
            {muted ? <MicOff sx={{ fontSize: 20 }} /> : <Mic sx={{ fontSize: 20 }} />}
          </MicButton>
        </Tooltip>
      </Group>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top-end"
        sx={theme => ({ zIndex: theme.zIndex.tooltip })}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <MenuPaper>
            {outputs.length > 0 && (
              <DeviceList
                label={I18n.t('VoiceMode.speaker')}
                items={outputs}
                activeId={devices?.activeOutput}
                kind="audiooutput"
                onSwitchDevice={handleSwitch}
              />
            )}

            <MeterDivider aria-hidden="true">
              <Mic sx={{ fontSize: 14, flexShrink: 0, color: 'text.secondary' }} />
              <MeterTrack>
                <MeterFill />
              </MeterTrack>
            </MeterDivider>

            <DeviceList
              label={I18n.t('VoiceMode.microphone')}
              items={inputs}
              activeId={devices?.activeInput}
              kind="audioinput"
              onSwitchDevice={handleSwitch}
            />
          </MenuPaper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

const deviceShape = shape({ deviceId: string, label: string });

MicControl.propTypes = {
  muted: bool,
  onToggleMute: func,
  devices: shape({
    inputs: arrayOf(deviceShape),
    outputs: arrayOf(deviceShape),
    activeInput: string,
    activeOutput: string,
  }),
  onSwitchDevice: func,
};

export default MicControl;
